import { Loader, Modal } from "@caprikorn/dynamo-ui";
import { type FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { generatePath, useNavigate } from "react-router-dom";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { FORM_VALIDATION_MODE } from "@core/core.constants";
import { ROUTES } from "@router/router.constants";
import { useGetCustomersAllByGroup } from "@services/apiEcms/useGetCustomersAllByGroup";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useGetUserEmailCheck } from "@services/apiEcms/useGetUserEmailCheck";
import { formatCaughtError } from "@utils/error.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { useCreateUser } from "../AccessManager.hooks";
import type { NewUserForm } from "../AccessManager.types";
import classes from "./CreateUser.module.css";
import { AccessErrors } from "../AccessErrors/AccessErrors";
import { AuthorizationsDrawer } from "../AuthorizationsDrawer/AuthorizationsDrawer";
import { CreateUserForm } from "../CreateUserForm/CreateUserForm";
import { RoleDrawer } from "../RoleDrawer/RoleDrawer";
import type { AccessRole } from "../RoleDrawer/RoleDrawer.types";

export const CreateUser: FC = () => {
  const navigate = useNavigate();
  const { t } = useCustomTranslation("main");
  const { currentGroup } = useGroupReferences();

  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [showRoleDrawer, setShowRoleDrawer] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [showAuthorizationsDrawer, setShowAuthorizationsDrawer] =
    useState<boolean>(false);

  const {
    data: groupDetails,
    isLoading,
    refetch: groupRefetch,
    error,
  } = useGetGroup({ groupId: currentGroup });

  const isGroupAdmin = groupDetails?.isAdmin ?? false;

  // Null authorizations are giving access to whole group perimeter
  const [perimeterAuthorizations, setPerimeterAuthorizations] = useState<
    string[] | null
  >(null);

  const methods = useForm<NewUserForm>({ mode: FORM_VALIDATION_MODE });
  const { handleSubmit, trigger, getValues } = methods;

  const toggleAuthorizationsDrawer = () =>
    setShowAuthorizationsDrawer(!showAuthorizationsDrawer);
  const toggleRoleDrawer = () => setShowRoleDrawer(!showRoleDrawer);

  const onOpenModal = () =>
    trigger(["firstName", "lastName"]).then(
      (isValid) => isValid && setShowConfirmationModal(true),
    );

  const {
    mutate: checkEmailMutate,
    data: emailData,
    isPending: isLoadingCheckMail,
    reset: resetCheckEmail,
  } = useGetUserEmailCheck();

  const {
    mutateFunction: createUserMutate,
    isPending: isCreatePending,
    isSuccess,
    data: createUserData,
  } = useCreateUser();

  const {
    data: customerByGroupData,
    isPending: isLoadingCustomerByGroups,
    isError: isErrorCustomerByGroups,
    refetch,
  } = useGetCustomersAllByGroup();

  const onSubmitPerimeterAuthorizations = (authorizations: string[]) => {
    const formatedAuthorizations =
      authorizations.length === customerByGroupData?.length
        ? null
        : authorizations;
    setPerimeterAuthorizations(formatedAuthorizations);
    toggleAuthorizationsDrawer();
  };

  const onSubmitRole = (role: AccessRole) => {
    setIsAdmin(role === "ADMIN");
    if (role === "ADMIN") {
      setPerimeterAuthorizations(null);
    }
    toggleRoleDrawer();
  };

  const onSubmit = (newUser: NewUserForm) => {
    createUserMutate({
      ...newUser,
      authorizations: perimeterAuthorizations,
      isAdmin,
    });
  };

  if (isLoading) {
    return <Loader size="lg" />;
  }

  if (!isGroupAdmin || error) {
    return (
      <AccessErrors
        errorStatus={error?.status ? formatCaughtError(error).status : 403}
        location="create"
        refetch={groupRefetch}
      />
    );
  }

  return (
    <form className={classes.form}>
      <FormProvider {...methods}>
        <CreateUserForm
          checkEmail={() => {
            const email = getValues("email");
            trigger(["email"]).then(
              (isValid) => isValid && checkEmailMutate(email),
            );
          }}
          data={emailData?.data}
          isAdmin={isAdmin}
          isLoading={isLoadingCheckMail || isLoadingCustomerByGroups}
          onToggleAuthorizationsDrawer={toggleAuthorizationsDrawer}
          onToggleModal={onOpenModal}
          onToggleRoleDrawer={toggleRoleDrawer}
          perimeterAuthorizationsNbr={
            perimeterAuthorizations?.length ?? customerByGroupData?.length
          }
          resetCheckEmail={resetCheckEmail}
        />
      </FormProvider>
      {showAuthorizationsDrawer && (
        <AuthorizationsDrawer
          currentAuthorizations={
            perimeterAuthorizations === null
              ? customerByGroupData
              : customerByGroupData?.filter(({ customerId }) =>
                  perimeterAuthorizations?.includes(customerId),
                )
          }
          customerList={customerByGroupData}
          isError={isErrorCustomerByGroups}
          onHide={toggleAuthorizationsDrawer}
          onSubmit={onSubmitPerimeterAuthorizations}
          refetch={refetch}
        />
      )}
      {showRoleDrawer && (
        <RoleDrawer
          isAdmin={isAdmin}
          isSubmitting={false}
          onHide={toggleRoleDrawer}
          onSubmit={onSubmitRole}
        />
      )}
      {showConfirmationModal && (
        <Modal maxSize="md" onClose={() => setShowConfirmationModal(false)}>
          <Modal.Header
            illustration="question"
            title={t("userCreate.confirm.title")}
          />
          <Modal.Body>
            <p>{t("userCreate.confirm.subtitle")}</p>
            <p>{t("userCreate.confirm.text")}</p>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.ConfirmButton
              isLoading={isCreatePending}
              onClick={handleSubmit(onSubmit)}
              type="submit"
            >
              {t("userCreate.confirm.submit")}
            </Modal.Footer.ConfirmButton>
            <Modal.Footer.CancelButton
              onClick={() => setShowConfirmationModal(false)}
            >
              {t("userCreate.confirm.cancel")}
            </Modal.Footer.CancelButton>
          </Modal.Footer>
        </Modal>
      )}
      {isSuccess && (
        <Modal maxSize="md" onClose={() => navigate(ROUTES.ACCESS_MANAGER)}>
          <Modal.Header
            illustration="perfect"
            title={t("userCreate.success.title")}
          />
          <Modal.Body>
            <p>{t("userCreate.success.text")}</p>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.ConfirmButton
              onClick={() =>
                navigate(
                  generatePath(ROUTES.ACCESS_MANAGER_USER, {
                    userId: createUserData?.id || "",
                  }),
                )
              }
            >
              {t("userCreate.success.user")}
            </Modal.Footer.ConfirmButton>
            <Modal.Footer.CancelButton
              onClick={() => navigate(ROUTES.ACCESS_MANAGER)}
            >
              {t("userCreate.success.list")}
            </Modal.Footer.CancelButton>
          </Modal.Footer>
        </Modal>
      )}
    </form>
  );
};
